import { Injectable } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { AddressSummary, CreateAddressRequest, UpdateAddressRequest } from '@cart/shared-types';
import { StoreContextService } from '../tenancy/store-context.service';

export abstract class AddressesRepository {
  abstract list(userId: string): Promise<AddressSummary[]>;
  abstract create(userId: string, input: CreateAddressRequest): Promise<AddressSummary>;
  abstract update(
    userId: string,
    addressId: string,
    input: UpdateAddressRequest
  ): Promise<AddressSummary | undefined>;
  abstract delete(userId: string, addressId: string): Promise<boolean>;
}

@Injectable()
export class PrismaAddressesRepository implements AddressesRepository {
  constructor(
    private readonly prisma: PrismaService,
    private readonly storeContext?: StoreContextService
  ) {}

  async list(userId: string): Promise<AddressSummary[]> {
    const addresses = await this.prisma.address.findMany({
      where: { storeId: this.storeId(), userId },
      orderBy: { createdAt: 'desc' },
    });

    return addresses.map((address) => this.toSummary(address));
  }

  async create(userId: string, input: CreateAddressRequest): Promise<AddressSummary> {
    const address = await this.prisma.address.create({
      data: {
        storeId: this.storeId(),
        userId,
        ...input,
      },
    });

    return this.toSummary(address);
  }

  async update(
    userId: string,
    addressId: string,
    input: UpdateAddressRequest
  ): Promise<AddressSummary | undefined> {
    const existing = await this.prisma.address.findFirst({
      where: { id: addressId, storeId: this.storeId(), userId },
      select: { id: true },
    });

    if (!existing) {
      return undefined;
    }

    const address = await this.prisma.address.update({
      where: { id: addressId },
      data: input,
    });

    return this.toSummary(address);
  }

  async delete(userId: string, addressId: string): Promise<boolean> {
    const existing = await this.prisma.address.findFirst({
      where: { id: addressId, storeId: this.storeId(), userId },
      select: { id: true },
    });

    if (!existing) {
      return false;
    }

    await this.prisma.address.delete({ where: { id: addressId } });

    return true;
  }

  private toSummary(address: {
    id: string;
    label: string;
    recipientName: string;
    line1: string;
    line2: string | null;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    phone: string | null;
    createdAt: Date;
  }): AddressSummary {
    return {
      id: address.id,
      label: address.label,
      recipientName: address.recipientName,
      line1: address.line1,
      line2: address.line2 ?? undefined,
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
      country: address.country,
      phone: address.phone ?? undefined,
      createdAt: address.createdAt.toISOString(),
    };
  }

  private storeId(): string {
    return this.storeContext?.storeId() ?? 'store_demo';
  }
}

@Injectable()
export class InMemoryAddressesRepository implements AddressesRepository {
  private readonly addresses: Array<AddressSummary & { userId: string }> = [
    {
      id: 'addr-001',
      userId: 'user-002',
      label: 'Home',
      recipientName: 'Shopper One',
      line1: '100 Market Street',
      city: 'San Francisco',
      state: 'CA',
      postalCode: '94105',
      country: 'US',
      phone: '555-0100',
      createdAt: '2026-05-16T09:00:00.000Z',
    },
  ];

  async list(userId: string): Promise<AddressSummary[]> {
    return this.addresses
      .filter((address) => address.userId === userId)
      .map((address) => this.toPublicAddress(address));
  }

  async create(userId: string, input: CreateAddressRequest): Promise<AddressSummary> {
    const address = {
      id: `addr-${String(this.addresses.length + 1).padStart(3, '0')}`,
      userId,
      ...input,
      createdAt: new Date().toISOString(),
    };

    this.addresses.push(address);

    return this.toPublicAddress(address);
  }

  async update(
    userId: string,
    addressId: string,
    input: UpdateAddressRequest
  ): Promise<AddressSummary | undefined> {
    const address = this.addresses.find((item) => item.id === addressId && item.userId === userId);

    if (!address) {
      return undefined;
    }

    Object.assign(address, input);

    return this.toPublicAddress(address);
  }

  async delete(userId: string, addressId: string): Promise<boolean> {
    const index = this.addresses.findIndex((item) => item.id === addressId && item.userId === userId);

    if (index === -1) {
      return false;
    }

    this.addresses.splice(index, 1);

    return true;
  }

  private toPublicAddress(address: AddressSummary & { userId: string }): AddressSummary {
    return {
      id: address.id,
      label: address.label,
      recipientName: address.recipientName,
      line1: address.line1,
      line2: address.line2,
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
      country: address.country,
      phone: address.phone,
      createdAt: address.createdAt,
    };
  }
}
